<script lang="ts" setup>
import type { Location } from 'vue-router';

// @ts-expect-error vue2-teleport types
import Vue2Teleport from 'vue2-teleport';
import { useRoute, useRouter } from 'vue-router/composables';

import { provideReactiveViewport } from '@/composables/useGlobalReactiveViewport';

import { deviceIdPlugin } from './plugins/aml-fingerprint';
import { formatPhone } from './utils/phone';

import { useAccountsStore } from './store/accounts';
import { useGlobalAppState } from './store/app-state';
import { useProjectStore } from './store/project';
import { useSidepanelStore } from './store/side-panel';
import { useProfileData } from './store/user-data';

import CircleAlertIcon from '@/components/icons/CircleAlertIcon.vue';
import CircleSuccessIcon from '@/components/icons/CircleSuccessIcon.vue';

const sidepanelStore = useSidepanelStore();
const visibleSidePanel = computed(() => sidepanelStore.visibleSidePanel);
const globalAppState = useGlobalAppState();
const { setRole } = useProjectStore();
const profileData = useProfileData({ init: false });
const { setUserCompanies } = useAccountsStore();
const { setDeviceId, updateWithServerEntry } = globalAppState;
const { isQuickAccessOn, hasDeviceId, isSessionExpired } = storeToRefs(globalAppState);
deviceIdPlugin.onReady(setDeviceId);
const router = useRouter();
const route = useRoute();
const mainRouterViewKey = computed(() => route.name === 'board' ? route.path + globalAppState.role : route.path);

watch(() => profileData.data, (nv) => {
  const borrowerEntry = nv?.companys?.find((c) => c.type?.includes('borr')) ?? null;
  const investorEntry = nv?.companys?.find((c) => c.type?.includes('inv')) ?? null;
  const formattedPhone = nv && formatPhone(nv.phone);

  if (!nv)
    return;

  setUserCompanies({
    borrower: borrowerEntry?.id
      ? {
          companyId: borrowerEntry.id,
          name: borrowerEntry.name || formattedPhone!,
          phone: formattedPhone!,
          isInvestor: false,
          avatarLink: borrowerEntry.docs.find((d) => d.type === 'user-avatar')?.link,
          withoutAuth: true,
        }
      : null,
    investor: investorEntry?.id
      ? {
          companyId: investorEntry.id,
          name: investorEntry.name || formattedPhone!,
          phone: formattedPhone!,
          isInvestor: true,
          avatarLink: investorEntry.docs.find((d) => d.type === 'user-avatar')?.link,
          withoutAuth: true,
        }
      : null,
  });

  updateWithServerEntry({
    userId: nv.userid ?? null,
    isAuthenticated: !!nv.isAuthenticated,
    isQuickAccessOn: nv.pin_settings?.isQuickAccessOn ?? false,
    hasPinSet: nv.pin_settings?.hasPinSet ?? false,
    isPinNeverInstalled: !nv.pin_settings,
    companysIds: {
      borrower: borrowerEntry?.id ?? null,
      investor: investorEntry?.id ?? null,
    },
    unreadTickets: nv.total_unread_tickets ?? 0,
    isReferralParticipant: investorEntry?.type === 'invest.FL',
  });
});

watch(() => globalAppState.companyId, (nv) => {
  profileData.setCompanyId(nv);
}, { immediate: true });

watch(() => globalAppState.role, (nv) => {
  setRole(nv);
});

watch(isSessionExpired, (nv) => {
  if (!nv)
    return;

  sidepanelStore.despawn(null, true); // fixme: это озачает "закрыть все активные -- странно, да?"

  if (isQuickAccessOn && hasDeviceId) {
    const destination = { name: 'quickAccess' } as Location;

    !route.fullPath.includes('redirectTo') && (destination.query = { redirectTo: route.fullPath });
    router.push(destination);
    return;
  }

  router.push({ name: 'login' });
});

provideReactiveViewport();

const appBarHeight = ref(0);
const appBarMargin = ref(0);
function onAppBarResize(data: { contentRect: ResizeObserverEntry['contentRect'], computedStyle: CSSStyleDeclaration }) {
  appBarHeight.value = data.contentRect.height;
  appBarMargin.value = Number.parseInt(data.computedStyle.marginBottom);
}
</script>

<template>
  <v-app
    :style="{
      '--app-bar-height': `${appBarHeight}px`,
      '--app-bar-margin-bottom': `${appBarMargin}px`,
    }"
  >
    <Vue2Teleport to="body > #app">
      <NotificationsGroup
        v-slot="{
          visible,
        }"
        data-disallow-inert
        style="left: 50%; top: 1rem; transform: translateX(-50%)"
      >
        <BaseAlert
          v-for="notificationItem in visible"
          :key="`notification-${notificationItem.id}`"
          dismissible
          elevated
          :type="notificationItem.type"
          @dismiss="() => notificationItem.remove()"
          v-on="notificationItem.listeners"
        >
          <CircleSuccessIcon v-if="notificationItem.type === 'success'" />
          <CircleAlertIcon v-else />
          <span>{{ notificationItem.text }}</span>
        </BaseAlert>
      </NotificationsGroup>
    </Vue2Teleport>

    <router-view name="bar" @resize="onAppBarResize" />

    <div :class="$style['main-container']">
      <Transition name="slide-x-transition" mode="out-in">
        <router-view :key="mainRouterViewKey" />
      </Transition>
    </div>

    <router-view name="footer" />

    <component :is="visibleSidePanel.component" v-if="visibleSidePanel" :panel="visibleSidePanel" v-bind="visibleSidePanel.props" />
  </v-app>
</template>

<style lang="scss">
@import '@/styles/main.scss';

div#app.v-application {
  background-color: var(--app-background-color, transparent);
  transition: background-color 150ms ease-in;
}
</style>

<style lang="scss" module>
.main-container {
  --top-position: calc(var(--app-bar-height, 0) + var(--app-bar-margin-bottom, 0));
  min-height: calc(100vh - var(--top-position));
  padding-bottom: 24px;
  margin-bottom: 96px;
}
</style>
